<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  sequence: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['view', 'edit', 'delete', 'enroll']);

const { t } = useI18n();

const formatPercent = value => {
  if (!value) return '0%';
  return `${value.toFixed(1)}%`;
};

const triggerLabel = computed(() => {
  const key = (props.sequence.trigger_event || 'manual').toUpperCase();
  return t(`CAMPAIGN.FOLLOW_UP.TRIGGER.${key}`);
});
</script>

<template>
  <div class="rounded-lg border border-n-weak bg-n-solid-1 p-4">
    <div class="flex items-start justify-between">
      <div>
        <div class="flex items-center gap-2">
          <h3 class="text-lg font-medium text-n-slate-12">
            {{ sequence.name }}
          </h3>
          <span
            class="px-2 py-0.5 text-xs font-medium rounded-full"
            :class="
              sequence.active
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-alpha-2 text-n-slate-10'
            "
          >
            {{
              sequence.active
                ? t('CAMPAIGN.FOLLOW_UP.ACTIVE')
                : t('CAMPAIGN.FOLLOW_UP.INACTIVE')
            }}
          </span>
        </div>
        <p v-if="sequence.description" class="mt-1 text-sm text-n-slate-11">
          {{ sequence.description }}
        </p>
        <p class="mt-1 text-xs text-n-slate-10">
          {{ triggerLabel }}
        </p>
      </div>
    </div>

    <div class="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-5">
      <div>
        <p class="text-xs text-n-slate-10">
          {{ t('CAMPAIGN.FOLLOW_UP.STEPS') }}
        </p>
        <p class="text-lg font-semibold text-n-slate-12">
          {{ sequence.steps_count || 0 }}
        </p>
      </div>
      <div>
        <p class="text-xs text-n-slate-10">
          {{ t('CAMPAIGN.FOLLOW_UP.ENROLLED') }}
        </p>
        <p class="text-lg font-semibold text-n-slate-12">
          {{ sequence.total_enrolled || 0 }}
        </p>
      </div>
      <div>
        <p class="text-xs text-n-slate-10">
          {{ t('CAMPAIGN.FOLLOW_UP.COMPLETED') }}
        </p>
        <p class="text-lg font-semibold text-n-slate-12">
          {{ sequence.total_completed || 0 }}
        </p>
      </div>
      <div>
        <p class="text-xs text-n-slate-10">
          {{ t('CAMPAIGN.FOLLOW_UP.COMPLETION_RATE') }}
        </p>
        <p class="text-lg font-semibold text-n-blue-11">
          {{ formatPercent(sequence.completion_rate) }}
        </p>
      </div>
      <div>
        <p class="text-xs text-n-slate-10">
          {{ t('CAMPAIGN.FOLLOW_UP.CONVERSION_RATE') }}
        </p>
        <p class="text-lg font-semibold text-n-teal-11">
          {{ formatPercent(sequence.conversion_rate) }}
        </p>
      </div>
    </div>

    <div class="mt-4 flex flex-wrap items-center gap-2 text-xs text-n-slate-10">
      <span v-if="sequence.stop_on_reply" class="flex items-center gap-1">
        <span class="i-lucide-message-circle h-3 w-3" />
        {{ t('CAMPAIGN.FOLLOW_UP.STOP_ON_REPLY') }}
      </span>
      <span v-if="sequence.stop_on_click" class="flex items-center gap-1">
        <span class="i-lucide-mouse-pointer-click h-3 w-3" />
        {{ t('CAMPAIGN.FOLLOW_UP.STOP_ON_CLICK') }}
      </span>
      <span v-if="sequence.stop_on_optout" class="flex items-center gap-1">
        <span class="i-lucide-ban h-3 w-3" />
        {{ t('CAMPAIGN.FOLLOW_UP.STOP_ON_OPTOUT') }}
      </span>
    </div>

    <div class="mt-4 flex justify-end gap-2 border-t border-n-weak pt-4">
      <Button
        size="small"
        variant="faded"
        color="ruby"
        icon="i-lucide-trash-2"
        @click="emit('delete', sequence)"
      />
      <Button
        size="small"
        variant="faded"
        color="slate"
        icon="i-lucide-user-plus"
        @click="emit('enroll', sequence)"
      >
        {{ t('CAMPAIGN.FOLLOW_UP.ENROLL_BTN') }}
      </Button>
      <Button
        size="small"
        variant="faded"
        color="slate"
        icon="i-lucide-pencil"
        @click="emit('edit', sequence)"
      >
        {{ t('CAMPAIGN.FOLLOW_UP.EDIT_BTN') }}
      </Button>
      <Button size="small" icon="i-lucide-eye" @click="emit('view', sequence)">
        {{ t('CAMPAIGN.FOLLOW_UP.VIEW_BTN') }}
      </Button>
    </div>
  </div>
</template>
